import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MentionsInput, Mention } from 'react-mentions';
import Avatar from '@strava/ui/Avatar';

import I18n from 'utils/I18n';
import { setMentionInputStyles } from './MentionInputStyles';

import styles from './StravaMentionsInput.scss';
import { sortAndFilter } from './StravaMentionsInput.utils';
import { fetchAthletes } from './StravaMentionsInput.cache';

const StravaMentionsInput = ({
  viewingAthleteId,
  entity,
  entityId,
  placeHolder,
  suggestionLimit,
  autoFocus,
  trackAnalytics,
  onInputChange,
  clearInputToggle,
  inputHeight,
  value
}) => {
  const [mentionableAthletes, setMentionableAthletes] = useState([]);
  const [inputValue, setInputValue] = useState(value);
  const firstRender = useRef(true);

  const mentionInputStyles = setMentionInputStyles({ inputHeight });

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setInputValue('');
  }, [clearInputToggle]);

  useEffect(() => {
    let isSubscribed = true;
    fetchAthletes({ viewingAthleteId }).then((mentionableFollowers) => {
      if (isSubscribed) {
        setMentionableAthletes(mentionableFollowers);
      }
    });
    return () => {
      isSubscribed = false;
    };
  }, [entity, entityId, viewingAthleteId]);

  const nameFromId = (id) => {
    return mentionableAthletes.find((a) => a.id === parseInt(id, 10)).display;
  };

  const filteredMentionableAthletes = (query) => {
    const results = mentionableAthletes.filter(
      (a) => a.display.toLowerCase().indexOf(query.toLowerCase()) >= 0
    );
    return results.slice(0, suggestionLimit);
  };

  const fetchEntityAthletes = (e) => {
    if (e.key === '@') {
      fetchAthletes({
        entity,
        entityId,
        viewingAthleteId
      }).then((mentionableEntityOwners) => {
        const filtered = sortAndFilter(
          mentionableAthletes.concat(mentionableEntityOwners),
          viewingAthleteId
        );
        setMentionableAthletes(filtered);
      });
    }
  };

  const renderSuggestion = (entry) => {
    const avatarStyle = { marginRight: '8px' };
    return (
      <div className={styles.suggestion} data-testid="suggestions">
        <Avatar
          name={entry.display}
          src={entry.profile}
          size="small"
          type="athlete"
          style={avatarStyle}
          badge={entry.member_type}
          onClick={() => {
            trackAnalytics({
              action: 'click',
              element: 'profile_picture',
              properties: { element_source: 'mentionable-athlete-suggestions' }
            });
          }}
        />
        <div className={styles.metaContainer}>
          <div className={styles.athleteName}>{entry.display}</div>
          {entry.location && entry.location.length > 0 && (
            <div className={`text-caption2 ${styles.location}`}>
              {entry.location}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <MentionsInput
      allowSpaceInQuery={true}
      autoFocus={autoFocus}
      style={mentionInputStyles}
      classNames={{
        mentions: styles.body,
        mentions__highlighter: styles.highlighter,
        mentions__suggestions: styles.suggestions,
        mentions__suggestions__list: styles.suggestionList
      }}
      value={inputValue}
      placeholder={placeHolder}
      onChange={(event, newValue, newPlainTextValue, mentions) => {
        setInputValue(newValue);
        onInputChange({ event, newValue, newPlainTextValue, mentions });
      }}
      onKeyDown={fetchEntityAthletes}
      a11ySuggestionsListLabel={I18n.t('components.mentions.list_label')}
    >
      <Mention
        appendSpaceOnAdd={true}
        data={filteredMentionableAthletes}
        displayTransform={nameFromId}
        markup="[strava://athletes/__id__]"
        renderSuggestion={renderSuggestion}
        className={styles.mentionedAthlete}
      />
    </MentionsInput>
  );
};

StravaMentionsInput.defaultProps = {
  placeHolder: null,
  suggestionLimit: 5,
  autoFocus: false,
  trackAnalytics: () => {},
  onInputChange: () => {},
  clearInputToggle: false,
  inputHeight: 60,
  value: ''
};

StravaMentionsInput.propTypes = {
  entity: PropTypes.oneOf(['activity', 'group_activity', 'post']).isRequired,
  entityId: PropTypes.string.isRequired,
  placeHolder: PropTypes.string,
  suggestionLimit: PropTypes.number,
  autoFocus: PropTypes.bool,
  viewingAthleteId: PropTypes.string.isRequired,
  trackAnalytics: PropTypes.func,
  onInputChange: PropTypes.func,
  clearInputToggle: PropTypes.bool,
  inputHeight: PropTypes.number,
  value: PropTypes.string
};

export default StravaMentionsInput;
